import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

export interface User {
  id: string;
  email: string;
  fullName: string;
  department?: string | null;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'AGENT';
  organizationId: string | null;
  organization?: {
    id: string;
    name: string;
    slug: string;
    status: string;
    timezone: string;
    subscription?: {
      id: string;
      duration: string;
      status: string;
      isLifetime: boolean;
      expiresAt: string | null;
    };
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  remainingDays: number | null;
  login: (email: string, pass: string) => Promise<User>;
  registerOrg: (payload: {
    organizationName: string;
    adminEmail: string;
    adminPassword: string;
    adminFullName: string;
    licenseKey: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('pulse_token'));
  const [remainingDays, setRemainingDays] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refreshUser = async () => {
    try {
      if (!localStorage.getItem('pulse_token')) {
        setUser(null);
        setLoading(false);
        return;
      }
      const data = await api.auth.me();
      setUser(data.user);
      setRemainingDays(data.remainingDays);
    } catch (err) {
      console.warn('Authentication check failed:', err);
      localStorage.removeItem('pulse_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const login = async (email: string, pass: string): Promise<User> => {
    const data = await api.auth.login({ email, password: pass });
    localStorage.setItem('pulse_token', data.token);
    setToken(data.token);
    if (data.user) {
      setUser(data.user);
    }
    await refreshUser();
    return data.user;
  };

  const registerOrg = async (payload: {
    organizationName: string;
    adminEmail: string;
    adminPassword: string;
    adminFullName: string;
    licenseKey: string;
  }) => {
    const data = await api.auth.registerOrg(payload);
    localStorage.setItem('pulse_token', data.token);
    setToken(data.token);
    await refreshUser();
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch {
      // ignore
    } finally {
      localStorage.removeItem('pulse_token');
      setToken(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        remainingDays,
        login,
        registerOrg,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
